import React, { useEffect, useMemo, useRef, useState } from "react";
import { Globe2, Loader2, X } from "lucide-react";

import { BROKER_ALERT_TITLE_CLASS } from "@/components/Broker/brokerAlertUi";
import {
  TradeFilterSearchableSelectFullWidth,
  type TradeFilterSelectOption,
} from "@/components/trade-filter-selects";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { presentMutationError } from "@/utils/showApiErrorToast";
import {
  parseStrategyRdMarketsResult,
  runStrategyRdMarketsTest,
  type StrategyRdMarketsResult,
} from "../../../services/strategyRdMarketsApi";
import { formatStrategyRdRMultiple, formatStrategyRdWinRate } from "../../../utils/strategyRd";
import { strategyRdJobStageLabel } from "../../../utils/strategyRdJobs";
import {
  accountSettingsFieldDescriptionClassName,
  accountSettingsNestedCardClassName,
} from "../Profile/accountSettingsCardShell";
import { statsCardHeadlineClassName } from "../Statistics/components/statisticsCardHeadline";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { StrategyRdHoldoutVerdictChip } from "./StrategyRdHoldoutRow";
import { StrategyRdBadge, StrategyRdInfo, StrategyRdLabCard } from "./strategyRdLabUi";

const MAX = 8;
/** Shown where a figure is missing (never an em dash). */
const NONE = "N/A";
const CHOICES = ["XAUUSD", "XAGUSD", "EURUSD", "GBPUSD", "USDJPY", "BTCUSDT", "ETHUSDT", "SOLUSDT", "NAS100", "US30"];
const DEFAULT_PICK = ["XAUUSD", "EURUSD", "GBPUSD", "BTCUSDT", "ETHUSDT", "NAS100", "US30"];

const VERDICT: Record<string, { text: string; tone: "good" | "warn" | "bad" | "neutral" }> = {
  carries: { text: "The edge carries to most markets", tone: "good" },
  some_markets: { text: "Works on some markets", tone: "warn" },
  one_market: { text: "Mostly one market", tone: "bad" },
  no_market: { text: "Doesn't hold on any market", tone: "bad" },
  too_few_trades: { text: "Period too short to judge", tone: "neutral" },
  too_few_markets: { text: "Too few markets with trades", tone: "neutral" },
};

/** A small bar for R per trade, from a centre line (right = made money). */
function RBar({ value, max }: { value: number | null; max: number }): JSX.Element {
  const v = value ?? 0;
  const w = `${Math.min(1, Math.abs(v) / max) * 50}%`;
  return (
    <span className="relative inline-block h-2 w-14" aria-hidden>
      <span className="absolute inset-y-0 left-1/2 w-px bg-border" />
      <span
        className="absolute inset-y-0 rounded-[2px]"
        style={{ width: w, ...(v >= 0 ? { left: "50%" } : { right: "50%" }), background: v >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR }}
      />
    </span>
  );
}

function pf(v: number | null): string {
  return v == null ? NONE : v.toFixed(2);
}

function pfColor(v: number | null): string | undefined {
  return v == null ? undefined : v > 1 ? PNL_POSITIVE_COLOR : v < 1 ? PNL_NEGATIVE_COLOR : undefined;
}

/**
 * Robustness tab: the backtest's unchanged recipe on other instruments, side by side — a
 * strategy that only works on the market it was built on is usually fitted to that market.
 */
export function StrategyRdMarketsPanel({
  hypothesisId,
  appliedAt,
  symbol,
  initial,
  available = [],
}: {
  hypothesisId: string | null;
  appliedAt: string | null;
  symbol: string;
  /** The backtest's saved `last_markets` (raw), if any. */
  initial?: unknown;
  /** Every Lab market — any of them can be added to the test. */
  available?: string[];
}): JSX.Element {
  const [picked, setPicked] = useState<string[]>(DEFAULT_PICK);
  const [result, setResult] = useState<StrategyRdMarketsResult | null>(() => parseStrategyRdMarketsResult(initial));
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    setResult(parseStrategyRdMarketsResult(initial));
  }, [hypothesisId, initial]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const own = symbol.trim().toUpperCase();
  // The picked markets (never the backtest's own — it is always in the table).
  const others = picked.filter((c) => c !== own);
  // Every Lab market not picked yet (the suggested set when the Lab's list is not loaded).
  const pool = available.length > 0 ? available : CHOICES;
  const suggested = useMemo(
    () => CHOICES.filter((c) => c !== own && !picked.includes(c) && pool.includes(c)),
    [own, picked, pool]
  );
  // Suggested markets first, then every other addable one (the picker searches as you type).
  const addOptions = useMemo<TradeFilterSelectOption[]>(() => {
    const rest = pool.filter((m) => m !== own && !picked.includes(m) && !suggested.includes(m));
    return [...suggested, ...rest].map((m) => ({ value: m, label: m }));
  }, [pool, own, picked, suggested]);
  const full = others.length >= MAX;
  const add = (raw: string) => {
    const m = raw.trim().toUpperCase();
    if (!m || m === own) return;
    if (available.length > 0 && !available.includes(m)) return;
    setPicked((prev) => (prev.includes(m) || prev.filter((x) => x !== own).length >= MAX ? prev : [...prev, m]));
  };
  const toggle = (s: string) =>
    setPicked((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : prev.length >= MAX ? prev : [...prev, s]));
  const stale = Boolean(result && appliedAt && result.appliedAt && result.appliedAt !== appliedAt);

  const run = async () => {
    if (!hypothesisId || running) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    setStage("Queued…");
    try {
      const next = await runStrategyRdMarketsTest(
        hypothesisId,
        picked.filter((s) => s !== own),
        controller.signal,
        (p) => {
          const st = (p.stage ?? null) as ({ market?: string } & Parameters<typeof strategyRdJobStageLabel>[0]) | null;
          setStage(
            st?.market
              ? `Testing ${st.market}${st.total ? ` · ${(st.done ?? 0) + 1} of ${st.total}` : ""}`
              : strategyRdJobStageLabel(st) || "Working…"
          );
        }
      );
      setResult(next);
    } catch (err) {
      if (!controller.signal.aborted) presentMutationError(err, "Other markets test failed");
    } finally {
      if (abortRef.current === controller) {
        setRunning(false);
        setStage(null);
      }
    }
  };

  const verdict = result?.verdict ? VERDICT[result.verdict] : null;
  // This backtest's market first, then the rest best first (after costs when known).
  const sortedRows = useMemo(() => {
    const rows = result?.markets ?? [];
    const score = (m: (typeof rows)[number]) =>
      m.status !== "ok" ? -Infinity : (m.costsProfitFactor ?? m.profitFactor ?? -Infinity);
    return [...rows].sort((a, b) => Number(b.original) - Number(a.original) || score(b) - score(a));
  }, [result]);
  const maxAbsR = Math.max(0.05, ...sortedRows.map((m) => Math.abs(m.expectancyR ?? 0)));
  return (
    <StrategyRdLabCard
      title={
        <span className="flex items-center gap-1.5">
          Other markets
          <StrategyRdInfo title="Why test other markets">
            <p>
              We run this exact strategy (same setup, entry, stop, target, management and size) on other instruments,
              each over the same period, each with its own unseen period and typical costs.
            </p>
            <p>
              A strategy that only makes money on the market it was built on was usually fitted to that market's past.
              One whose edge shows up on several unrelated markets is far more likely to be real.
            </p>
            <p>
              <strong>Carries</strong>: profitable after costs and holding up on the unseen period in most markets.{" "}
              <strong>Period too short</strong>: run the backtest over a longer window first.
            </p>
          </StrategyRdInfo>
        </span>
      }
      description="The same recipe on other instruments. A real edge isn't tied to one market."
      data-strategy-rd-markets=""
    >
      <div className={cn(accountSettingsNestedCardClassName, "gap-5 p-5")} data-strategy-rd-markets-picker="">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className={BROKER_ALERT_TITLE_CLASS}>Markets to test</span>
            <p className={cn(accountSettingsFieldDescriptionClassName, "m-0")}>
              <span className="tabular-nums">
                {others.length} of {MAX}
              </span>{" "}
              picked. {!hypothesisId ? "Run a backtest first." : "About a second per market."}
            </p>
          </div>
          <div className="w-full sm:w-64" data-strategy-rd-markets-add="">
            <TradeFilterSearchableSelectFullWidth
              id="strategy-rd-markets-add"
              aria-label="Add market"
              value=""
              onValueChange={add}
              options={addOptions}
              disabled={running || full}
              placeholder={full ? `Up to ${MAX} markets` : "Add market"}
            />
          </div>
        </div>

        <div className="flex min-h-8 flex-wrap items-center gap-2">
          {others.map((c) => (
            // A filled brand badge (Reports type badge language), white text.
            <Badge
              key={c}
              className="h-7 gap-1 !rounded-[6px] !border-0 bg-[image:var(--gradient-primary)] pl-2.5 pr-1 font-[family-name:var(--font-primary)] text-xs font-medium normal-case tracking-normal !text-white shadow-none"
            >
              {c}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${c}`}
                disabled={running}
                onClick={() => toggle(c)}
                className="size-5 !rounded-[4px] !text-white hover:!bg-white/20"
              >
                <X className="size-3.5" aria-hidden />
              </Button>
            </Badge>
          ))}
          {others.length === 0 ? (
            <span className={accountSettingsFieldDescriptionClassName}>No markets picked yet. Add one above.</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-solid border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className={cn(accountSettingsFieldDescriptionClassName, "m-0")}>
            {running ? stage : "Each market runs over the same period, with its own unseen period and typical costs."}
          </p>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0 self-start rounded-[6px] px-4 sm:self-auto"
            disabled={!hypothesisId || running || others.length === 0}
            onClick={() => void run()}
          >
            {running ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Globe2 className="size-4" aria-hidden />}
            <span className="normal-case tracking-normal">
              {running ? "Testing…" : `Test ${others.length} market${others.length === 1 ? "" : "s"}`}
            </span>
          </Button>
        </div>
      </div>

      {result ? (
        <div className="flex flex-col gap-4">
          {verdict ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <StrategyRdBadge tone={verdict.tone}>{verdict.text}</StrategyRdBadge>
              <span className={accountSettingsFieldDescriptionClassName}>
                {result.judged >= 3
                  ? `${result.held.length} of ${result.judged} markets profitable after costs and held up unseen${
                      result.held.length ? ` (${result.held.join(", ")})` : ""
                    }.`
                  : `${result.profitable.length} of ${result.tested} markets profitable. Too few trades per market to judge; use a longer window.`}
                {stale ? " This is for an older version of this backtest. Run it again." : ""}
              </span>
            </div>
          ) : null}
          <div className={cn(accountSettingsNestedCardClassName, "overflow-x-auto")}>
            <table className="w-full min-w-[640px] text-left font-[family-name:var(--font-secondary)] text-sm tabular-nums text-gray-500 dark:text-white">
              <thead className="border-b border-solid border-border">
                <tr className={statsCardHeadlineClassName}>
                  <th className="px-4 py-3 font-medium">Market</th>
                  <th className="px-4 py-3 text-right font-medium">Trades</th>
                  <th className="px-4 py-3 text-right font-medium">Win rate</th>
                  <th className="px-4 py-3 text-right font-medium" title="Gross wins ÷ gross losses, before trading costs">PF · no costs</th>
                  <th className="px-4 py-3 text-right font-medium" title="Average result per trade in R (1R = the risk taken), comparable across markets">R / trade</th>
                  <th className="px-4 py-3 text-right font-medium" title="All trades added up, in R">Total R</th>
                  <th className="px-4 py-3 font-medium">Unseen period</th>
                  <th className="px-4 py-3 text-right font-medium" title="The same trades with that market's typical spread and overnight swap">PF · with costs</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((m) => (
                  <tr key={m.symbol} className={cn("border-t border-solid border-border first:border-t-0", m.original && "bg-[color:var(--brand-tropical-surf)]/[0.06]")}>
                    <td className="px-4 py-3 font-medium text-[color:var(--brand-tropical-surf)]">
                      {m.symbol}
                      {m.original ? <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-white">this backtest</span> : null}
                    </td>
                    {m.status === "ok" ? (
                      <>
                        <td className="px-4 py-3 text-right">{m.trades ?? NONE}</td>
                        <td className="px-4 py-3 text-right">{formatStrategyRdWinRate(m.winRate) ?? NONE}</td>
                        <td className="px-4 py-3 text-right font-semibold" style={{ color: pfColor(m.profitFactor) }}>
                          {pf(m.profitFactor)}
                        </td>
                        <td className="px-4 py-3 text-right" style={{ color: pfColor(m.expectancyR == null ? null : 1 + m.expectancyR) }}>
                          <span className="inline-flex items-center justify-end gap-2">
                            <RBar value={m.expectancyR} max={maxAbsR} />
                            {formatStrategyRdRMultiple(m.expectancyR) ?? NONE}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right" style={{ color: pfColor(m.sumR == null ? null : 1 + m.sumR) }}>
                          {formatStrategyRdRMultiple(m.sumR) ?? NONE}
                        </td>
                        <td className="px-4 py-3">
                          <StrategyRdHoldoutVerdictChip verdict={m.unseenVerdict} />
                        </td>
                        <td className="px-4 py-3 text-right" style={{ color: pfColor(m.costsProfitFactor) }}>
                          {pf(m.costsProfitFactor)}
                        </td>
                      </>
                    ) : (
                      <td colSpan={7} className="px-4 py-3 font-[family-name:var(--font-primary)]">
                        {m.message || (m.status === "no_trades" ? "No trades on this market." : "Couldn't test this market.")}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </StrategyRdLabCard>
  );
}
